import { useEffect, useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

type HelpModalProps = {
  /** Heading shown in the modal header. */
  title: string;
  /** Body content. Render the copy block here. */
  children: ReactNode;
  onClose: () => void;
};

/**
 * Reusable help modal — backdrop, X close, click-outside, Escape.
 *
 * Rendered through a React portal to document.body so `fixed inset-0`
 * is anchored to the viewport instead of an ancestor's containing
 * block. The top nav uses `backdrop-filter` (backdrop-blur), which
 * silently promotes itself to the containing block for any fixed
 * descendant — without the portal, the modal clips to a thin strip
 * across the top of the screen. Login's <main> doesn't trip this
 * trap today, but portaling unconditionally is the defensive default.
 *
 * Built bespoke rather than against a shadcn Dialog because the project
 * doesn't pull in @radix-ui/react-dialog.
 */
export function HelpModal({ title, children, onClose }: HelpModalProps) {
  const cardRef = useRef<HTMLDivElement>(null);

  // Escape key closes.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  // Lock body scroll while the modal is open so the page behind doesn't
  // bounce on iOS when the user scrolls the modal content.
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  const onBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    // Only dismiss when the click landed on the backdrop, not on the card.
    if (e.target === e.currentTarget) onClose();
  };

  // SSR guard — this app is a SPA, but keeps the helper safe to import
  // anywhere without crashing at module load.
  if (typeof document === 'undefined') return null;

  return createPortal(
    // Two-layer structure:
    //   - Outer: fixed full-viewport backdrop that owns the scroll.
    //   - Inner: min-h-full flex centering. min-h-full lets the flex
    //     parent be at least the viewport height so items-center
    //     anchors to the middle; if the modal grows taller than the
    //     viewport, the OUTER scroll lets the user reach the bottom
    //     instead of clipping content.
    // The naive `flex items-center` directly on the backdrop breaks
    // for tall modals — the centered child gets a negative top offset
    // and the overflow handler can't scroll above it.
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="help-modal-title"
      onClick={onBackdropClick}
      className="fixed inset-0 z-[100] bg-black/70 overflow-y-auto"
    >
      <div className="min-h-full flex items-center justify-center p-4">
        <div
          ref={cardRef}
          className="cabinet-card w-full max-w-md p-5 space-y-4"
          // Stop backdrop-click from firing when clicking inside the card.
          onClick={(e) => e.stopPropagation()}
        >
          <header className="flex items-start justify-between gap-3">
            <h2
              id="help-modal-title"
              className="font-display text-cabinet-gold text-lg uppercase tracking-widest leading-tight"
            >
              {title}
            </h2>
            <button
              type="button"
              onClick={onClose}
              aria-label="Close help"
              className="-mt-1 -mr-1 h-11 w-11 rounded-md text-cabinet-cream/60 hover:text-cabinet-cream hover:bg-cabinet-cream/5 flex items-center justify-center shrink-0"
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                aria-hidden="true"
              >
                <path
                  d="M6 6l12 12M6 18L18 6"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
              </svg>
            </button>
          </header>
          <div className="text-sm text-cabinet-cream/85 space-y-4 leading-relaxed">
            {children}
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}

/**
 * Round "?" icon button — opens a HelpModal. Inline SVG so we don't pull
 * in lucide-react just for a single glyph. Tap target ≥ 44px for mobile.
 * Gold color picks up from the casino accent palette.
 */
export function HelpButton({
  onClick,
  label = 'Help',
  className,
}: {
  onClick: () => void;
  label?: string;
  className?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      className={
        'h-11 w-11 inline-flex items-center justify-center rounded-full text-cabinet-gold/80 hover:text-cabinet-gold-bright hover:bg-cabinet-gold/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-cabinet-gold transition' +
        (className ? ` ${className}` : '')
      }
    >
      <svg
        width="22"
        height="22"
        viewBox="0 0 24 24"
        fill="none"
        aria-hidden="true"
      >
        <circle
          cx="12"
          cy="12"
          r="10"
          stroke="currentColor"
          strokeWidth="2"
        />
        <path
          d="M9.5 9.5a2.5 2.5 0 015 0c0 1.5-1.5 2-2.5 3v1"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <circle cx="12" cy="17" r="1.1" fill="currentColor" />
      </svg>
    </button>
  );
}
